import CostCenter from '@/components/Passenger/models/CostCenter';
import Passenger from '@/components/Passenger/models/Passenger';
import TransportationPermission, { QuotaTypeEnum, TransportationPermissionSourceEnum } from '@/components/Passenger/models/TransportationPermission';
import restService from '@/services/restService';

import { 
  IEntity, 
  IEntityImporter, 
  IEntityMapping, 
  IEntityMatchResult, 
  IExcelRow, 
  IPropertyUpdate, 
  ITransportationPermissionMatchRequest 
} from '@/typings/import';

import { EEntityMatchType } from '../../enums';
import PropertyUpdateHelper from '../PropertyUpdateHelper';
import TransportationPermissionEntity from './TransportationPermissionEntity';
import TransportationPermissionMapping from './TransportationPermissionMapping';

export interface ITransportationPermissionImporterParams {
  passengerEntities: Array<IEntity<Passenger>>;
}

export interface ITransportationPermissionPostParams {
  passenger: Passenger;
}

export default class TransportationPermissionImporter implements IEntityImporter<TransportationPermission> {
  data: IExcelRow[];
  mapping: IEntityMapping<TransportationPermission>;
  entities: Array<IEntity<TransportationPermission>>;
  
  constructor(data: IExcelRow[], mapping: IEntityMapping<TransportationPermission>) {
    this.data = data;
    this.mapping = mapping;
    this.entities = this.data.map((d, i) => new TransportationPermissionEntity(d, i, this.mapping));
  }

  async matchExisting(
    row: number,
    params?: ITransportationPermissionImporterParams
  ): Promise<TransportationPermission> {

    const entities = this.entities.filter(e => e.excelRowIndex === row);
    entities.forEach(e => {
      if (this.shouldCreateEntity(e)) {
        e.entity = this.createNewTransportationPermission(e);
        e.matchedBy = EEntityMatchType.Created;
      }
    });
   
    return null;
  }
  
  async postEntity(entity: IEntity<TransportationPermission>, params: ITransportationPermissionPostParams)
    : Promise<TransportationPermission> {

    const ptp = entity.entity;
    const passenger = params.passenger;
    if (!ptp || !passenger || !passenger.id) {
      return null;
    }
    // TODO: check if ptp can actually be posted. Or should we do that earlier?

    const doNotUpdateProperties = TransportationPermissionMapping.NON_ENTITY_MAPPINGS
    .concat(TransportationPermissionMapping.COSTCENTER_MAPPING);

    entity.propertyUpdates
    .filter(pu => !pu.doNotUpdate && !doNotUpdateProperties.includes(pu.propertyName))
    .filter(pu => this.shouldUpdateProperty(pu, entity.matchedBy))
    .forEach(pu => ptp[pu.propertyName]  = PropertyUpdateHelper.getValue(pu));

    const costCenterPropertyUpdate = entity.propertyUpdates
    .find(pu => pu.propertyName === TransportationPermissionMapping.COSTCENTER_MAPPING);
    
    const costCenter = this.createCostCenter(costCenterPropertyUpdate);
    
    ptp.costCenter = costCenter;
    ptp.passengerId = passenger.id;

    this.convertPtpQuotas(ptp);
    
    // We are only creating PTPs atm
    const createdPtp = await restService.transportationPermissions.createTransportationPermission(ptp);

    return new TransportationPermission(createdPtp);
  }
  convertPtpQuotas(ptp: TransportationPermission) {
    switch(ptp.quotaType) {
      case QuotaTypeEnum.Distance:
        ptp.initialQuota = ptp.initialQuota * 1000;
        ptp.initialExpressQuota = ptp.initialExpressQuota * 1000;
        break;
      case QuotaTypeEnum.Currency:
        ptp.initialQuota = ptp.initialQuota * 100;
        ptp.initialExpressQuota = ptp.initialExpressQuota * 100;
        break;
      default:
        break;
    }
  }

  onMappingChanged(mapping: IEntityMapping<TransportationPermission>) {
    this.mapping = mapping;
    this.entities.forEach(e => e.onMappingChanged(this.mapping, this.data[e.excelRowIndex]));
  }

  private createNewTransportationPermission(entity: IEntity<TransportationPermission>) {
    const propertyUpdates = entity.propertyUpdates
      .filter(pu => !TransportationPermissionMapping.NON_ENTITY_MAPPINGS.includes(pu.propertyName));

    const ptp = new TransportationPermission(null);
    propertyUpdates.forEach(pu => ptp[pu.propertyName] = PropertyUpdateHelper.getValue(pu));
    ptp.source = TransportationPermissionSourceEnum.Timit;
    return ptp;
  }

  private isCreationColumnDefined() {
    const mappingProperty = this.mapping.props
    .find(p => p.propertyName === TransportationPermissionMapping.ENTITY_CREATION_MAPPING);
      
    return mappingProperty && mappingProperty.cell;
  }

  private shouldCreateEntity(entity: IEntity<TransportationPermission>) {
    if (!this.isCreationColumnDefined())  {
      return true;
    }

    const creationPropertyUpdate = entity.propertyUpdates
    .find(pu => pu.propertyName === TransportationPermissionMapping.ENTITY_CREATION_MAPPING);

    if (!creationPropertyUpdate) {
      return false;
    }

    const value = PropertyUpdateHelper.getNewValueAsPropertyValue<string>(creationPropertyUpdate);
    if (value) {
      return true;
    }

    return false;
  }

  private createCostCenter(propertyUpdate: IPropertyUpdate<any>) {
    if (!propertyUpdate) {
      return null;
    }
    const name =  PropertyUpdateHelper.getValue(propertyUpdate);
    if (!name) {
      return null;
    }

    return new CostCenter({ name });
  }

  private shouldUpdateProperty(propertyUpdate: IPropertyUpdate<any>, matchedBy: EEntityMatchType) {
    if (matchedBy === EEntityMatchType.Created) {
      return true;
    }
    // Keep matched original values if no mapping has been given
    const mappingProperty = this.mapping.props.find(p => p.propertyName === propertyUpdate.propertyName);
    if (!mappingProperty) {
      return false;
    }
    return PropertyUpdateHelper.propertyMappingIsDefined(mappingProperty);
  }
  
}
